// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: free/busy of the selected attendees over the chosen window, so a
// clash is visible before the invite goes out. Debounced so typing a time or
// toggling chips does not fire a Graph call per keystroke.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {
	type AvailabilityRequest,
	availabilityRequests,
	type ExistingAvailabilityEvent,
} from '@app/features/konvo_calendar/utils/KonvoAvailability';
import type {KonvoMailAddress} from '@app/features/konvo_mail/state/KonvoMail';
import {http} from '@app/features/platform/transport/RestTransport';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useEffect, useState} from 'react';

const BUSY_DESCRIPTOR = msg({
	message: '{name} is busy {when}',
	comment: 'Conflict line on an event form. when is a time range, optionally followed by the clashing item.',
});
const TENTATIVE_DESCRIPTOR = msg({
	message: '{name} has a tentative item {when}',
	comment: 'Conflict line on an event form for a tentative clash.',
});

export interface AvailabilityItem {
	status: string;
	subject: string | null;
	start: string | null;
	end: string | null;
}
export interface AvailabilityPerson {
	address: string;
	status: 'free' | 'busy' | 'tentative' | 'unknown';
	items: Array<AvailabilityItem>;
}
export interface AttendeeConflict {
	address: string;
	status: 'busy' | 'tentative';
	text: string;
}

/**
 * `start`/`end` are local `YYYY-MM-DDTHH:MM` form values. Returns per-address
 * status plus ready-to-render conflict lines.
 */
export function useAttendeeAvailability(
	i18n: I18n,
	attendees: ReadonlyArray<KonvoMailAddress>,
	start: string,
	end: string,
	enabled: boolean,
	existing?: ExistingAvailabilityEvent | null,
): {
	checking: boolean;
	checked: boolean;
	statusFor: (address: string) => AvailabilityPerson['status'] | null;
	conflicts: Array<AttendeeConflict>;
} {
	const [availability, setAvailability] = useState<Map<string, AvailabilityPerson>>(new Map());
	const [checking, setChecking] = useState(false);
	const attendeeKey = attendees
		.map((entry) => entry.address.toLowerCase())
		.sort()
		.join(',');
	const requestKey = JSON.stringify(
		enabled ? availabilityRequests(attendeeKey ? attendeeKey.split(',') : [], start, end, existing) : [],
	);
	useEffect(() => {
		const requests: Array<AvailabilityRequest> = JSON.parse(requestKey);
		setAvailability(new Map());
		if (requests.length === 0) {
			setChecking(false);
			return;
		}
		let cancelled = false;
		setChecking(true);
		const timer = setTimeout(async () => {
			try {
				const responses = await Promise.all(
					requests.map((body) =>
						http.post<{connected: boolean; people?: Array<AvailabilityPerson>}>(Endpoints.KONVO_CALENDAR_AVAILABILITY, {
							body,
						}),
					),
				);
				if (cancelled) return;
				const people = new Map<string, AvailabilityPerson>();
				const rank = {free: 0, unknown: 1, tentative: 2, busy: 3};
				for (const response of responses) {
					for (const person of response.body?.people ?? []) {
						const address = person.address.toLowerCase();
						const previous = people.get(address);
						people.set(address, {
							...person,
							status: previous && rank[previous.status] > rank[person.status] ? previous.status : person.status,
							items: [...(previous?.items ?? []), ...person.items],
						});
					}
				}
				setAvailability(people);
			} catch {
				if (!cancelled) setAvailability(new Map());
			} finally {
				if (!cancelled) setChecking(false);
			}
		}, 500);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [requestKey]);

	const time = new Intl.DateTimeFormat(i18n.locale, {hour: '2-digit', minute: '2-digit'});
	const conflicts: Array<AttendeeConflict> = [];
	for (const entry of attendees) {
		const person = availability.get(entry.address.toLowerCase());
		if (!person || (person.status !== 'busy' && person.status !== 'tentative')) continue;
		const first =
			person.items.find((item) =>
				person.status === 'busy' ? item.status === 'busy' || item.status === 'oof' : item.status === 'tentative',
			) ?? person.items[0];
		const when =
			first?.start && first.end
				? `${time.format(new Date(first.start))}–${time.format(new Date(first.end))}${first.subject ? ` · ${first.subject}` : ''}`
				: '';
		conflicts.push({
			address: entry.address,
			status: person.status,
			text: i18n._(person.status === 'busy' ? BUSY_DESCRIPTOR : TENTATIVE_DESCRIPTOR, {
				name: entry.name || entry.address,
				when,
			}),
		});
	}
	return {
		checking,
		// An edit checks only additional time; do not claim everyone is free for the full event.
		checked: !existing && availability.size > 0,
		statusFor: (address) => availability.get(address.toLowerCase())?.status ?? null,
		conflicts,
	};
}
